import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { TooltipContentWithCloseButton } from '@/components/tooltip/tooltip-content-with-close-button';
import { DisruptionAlertModel } from '@/lib/models/disruption-alert/types';
import { tv } from '@/utils/styles';

interface Props {
  alert: DisruptionAlertModel;
}

const DisruptionAlertMarker = ({ alert }: Props) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const { base, tooltipContent, text, link, icon } = styles({ tooltipIsOpen: showTooltip });

  return (
    <Tooltip
      content={
        <TooltipContentWithCloseButton onClose={() => setShowTooltip(!showTooltip)} closeButtonTop>
          <div className={tooltipContent()}>
            <Heading variant="h6">{alert.title}</Heading>
            <Text variant="sm" className={text()}>
              {alert.description}
            </Text>
            <Link href={alert.url} target="_blank" className={link()}>
              Learn more
            </Link>
          </div>
        </TooltipContentWithCloseButton>
      }
      isOpen={showTooltip}
      variant="white"
      whileElementsMountedOptions={{
        animationFrame: true,
      }}
      onOpenChange={() => setShowTooltip(!showTooltip)}
      trigger={['click']}
    >
      <div className={base()}>
        <AlertTriangle className={icon()} />
      </div>
    </Tooltip>
  );
};

const styles = tv({
  slots: {
    base: 'relative -ml-2 -mt-2 grid h-6 w-6 place-items-center rounded-full bg-errorDark transition-all before:absolute before:-left-3 before:-top-3 before:h-12 before:w-12 before:rounded-full before:bg-errorDark before:opacity-10 before:content-[""]',
    tooltipContent: 'w-64 px-1.5 py-2.5 text-left',
    icon: '-mt-[2px] h-4 w-4 text-white',
    link: 'mt-2 inline-block text-sm leading-normal underline hover:no-underline',
    text: 'm-0 mt-2 leading-normal',
  },
  variants: {
    tooltipIsOpen: {
      false: {
        base: 'hover:scale-110',
      },
    },
  },
});

export { DisruptionAlertMarker };
